import FadeInPanel from '@/shared/Animated/FadeInPanel'
import usePopup from '@/shared/hooks/usePopup'
import { IOption, Primitive } from '@/types/global'
import cn from '@/utils/cn'
import css from './dropdownMultiFilterChips.module.css'
import { CSSProperties, useMemo, useRef, useState } from 'react'
import ArrowDown from '../../../../assets/icons/arrowDown.svg?react'
import Close from '../../../../assets/icons/close.svg?react'
import Button from '@/shared/Buttons/Button'
import DropdownInputListItem from '@/shared/Dropdowns/DropdownInputListItem'

interface IProps<T extends Primitive> {
  options: IOption<T>[]
  onSelect: (options: T[], name: string) => void
  selectedOptions: T[]

  panelStyle?: CSSProperties
  title?: string
  disabled?: boolean
  style?: CSSProperties
  name?: string
  loading?: boolean
}

const DropdownMultiFilterChips = <T extends Primitive>(props: IProps<T>) => {
  const [tempSelectedOptions, setTempSelectedOptions] = useState<Set<T>>(new Set(props.selectedOptions))
  const ref = useRef<HTMLInputElement>()
  const { open, setOpen } = usePopup(ref)

  const handleSelect = (option: IOption<T>) => {
    setTempSelectedOptions((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(option.value)) {
        newSet.delete(option.value)
      } else {
        newSet.add(option.value)
      }
      return newSet
    })
  }

  const preparedOptions = useMemo(() => {
    const sortedOptions = props.options.sort((a, b) => {
      const aSelected = props.selectedOptions.includes(a.value)
      const bSelected = props.selectedOptions.includes(b.value)

      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1
      return 0
    })
    return sortedOptions
  }, [props.options, open, props.selectedOptions])

  const renderOptions = () => {
    return (
      <div className={css.options}>
        {preparedOptions.map((option, index) => (
          <DropdownInputListItem
            key={index}
            onClick={(ev) => {
              ev.stopPropagation()
              handleSelect(option)
            }}
            active={tempSelectedOptions.has(option.value)}
          >
            <p className="text-nm">{option.title}</p>
          </DropdownInputListItem>
        ))}
      </div>
    )
  }

  const renderInput = () => {
    const tip =
      props.selectedOptions.length === 0 || props.selectedOptions.length === props.options.length
        ? 'Все'
        : `Выбрано ${props.selectedOptions.length}`
    return (
      <div>
        <span data-filled={props.selectedOptions.length > 0} className={cn('text-nm line-clamp', css.inputText)}>
          {props.title}
          <span className={cn(css.inputTextTip, 'text-heading-6')}>{tip}</span>
        </span>
      </div>
    )
  }

  const renderFooter = () => {
    if (props.loading) return null
    const isFooterDisabled = tempSelectedOptions.size === 0
    const clearButtonTip = isFooterDisabled ? '' : `(${tempSelectedOptions.size})`
    return (
      <div
        className={cn('flex-space-between', css.btnContainer)}
        onClick={(ev) => {
          ev.stopPropagation()
        }}
      >
        <Button
          size={'default'}
          type={'tetriary'}
          text={`Сбросить ${clearButtonTip}`}
          disabled={isFooterDisabled}
          onClick={() => {
            setTempSelectedOptions(new Set()), props.onSelect([], props.name)
          }}
        />
        <Button
          size={'default'}
          type={'primary'}
          disabled={isFooterDisabled}
          text="Применить"
          onClick={() => {
            props.onSelect([...tempSelectedOptions], props.name), setOpen(false)
          }}
        />
      </div>
    )
  }

  const renderClose = () => {
    if (!props.selectedOptions || props.selectedOptions.length === 0) return null
    return (
      <div
        className={css.closeIcon}
        onClick={(e) => {
          e.stopPropagation()
          setTempSelectedOptions(new Set()), props.onSelect([], props.name)
        }}
      >
        <Close width={8.5} height={8.5} />
      </div>
    )
  }

  return (
    <div
      className={css.wrapper}
      style={props.style}
      data-filled={props.selectedOptions.length > 0}
      data-open={open}
      ref={ref}
      onClick={!props.disabled ? () => setOpen((prev) => !prev) : null}
    >
      {renderInput()}
      {!props.disabled && (
        <ArrowDown data-open={open} data-filled={tempSelectedOptions.size > 0} className={css.arrow} />
      )}
      {renderClose()}
      <FadeInPanel open={open} className={css.fadeInPanel} style={props.panelStyle}>
        {renderOptions()}
        {renderFooter()}
      </FadeInPanel>
    </div>
  )
}

export default DropdownMultiFilterChips
